import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Lock, Check, Eye, EyeOff, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const brandStyles = {
  primary: 'bg-[#2B8B3B] text-white hover:bg-[#236B2F]',
  secondary: 'bg-[#F5F5DC] text-[#2B8B3B] hover:bg-[#f0ebe1] hover:text-[#2B8B3B] border-[#2B8B3B]',
  accent: 'bg-[#F5F5DC]',
  text: 'text-[#2B8B3B]'
};

interface AccountPageProps {
  onNavigate: (page: 'dashboard' | 'bookings' | 'payment' | 'account', scrollToDistro?: boolean) => void;
}

interface Preferences {
  emailNotifications: boolean;
  marketingCommunications: boolean;
}

export default function AccountPage({ onNavigate }: AccountPageProps) {
  const { user } = useAuth();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [prefSaved, setPrefSaved] = useState(false);

  const emptyProfile = { firstName: '', lastName: '', email: '', phone: '', city: '', state: '' };
  const [profileData, setProfileData] = useState(emptyProfile);
  const [tempProfileData, setTempProfileData] = useState(emptyProfile);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [preferences, setPreferences] = useState<Preferences>({
    emailNotifications: true,
    marketingCommunications: false,
  });

  // Load profile and preferences from Supabase
  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('first_name, last_name, phone, city, state, notif_email_bookings, notif_email_cancellations, notif_email_refunds, notif_marketing')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        const loaded = {
          firstName: data?.first_name ?? '',
          lastName: data?.last_name ?? '',
          email: user.email ?? '',
          phone: data?.phone ?? '',
          city: data?.city ?? '',
          state: data?.state ?? '',
        };
        setProfileData(loaded);
        setTempProfileData(loaded);

        // All three transactional prefs share one UI toggle; true if ANY is true (default true)
        const emailOn =
          data?.notif_email_bookings !== false &&
          data?.notif_email_cancellations !== false &&
          data?.notif_email_refunds !== false;
        setPreferences({
          emailNotifications: emailOn,
          marketingCommunications: data?.notif_marketing === true,
        });

        setProfileLoading(false);
      });
  }, [user]);

  const handlePrefChange = async (key: keyof Preferences, value: boolean) => {
    if (!user) return;
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);

    // Map UI toggles to DB columns
    const dbUpdate: Record<string, boolean> = {};
    if (key === 'emailNotifications') {
      dbUpdate.notif_email_bookings = value;
      dbUpdate.notif_email_cancellations = value;
      dbUpdate.notif_email_refunds = value;
    } else if (key === 'marketingCommunications') {
      dbUpdate.notif_marketing = value;
    }

    const { error } = await supabase.from('profiles').update(dbUpdate).eq('id', user.id);
    if (!error) {
      setPrefSaved(true);
      setTimeout(() => setPrefSaved(false), 2000);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaveLoading(true);
    setSaveError('');
    const { error } = await supabase
      .from('profiles')
      .update({
        first_name: tempProfileData.firstName,
        last_name: tempProfileData.lastName,
        phone: tempProfileData.phone,
        city: tempProfileData.city,
        state: tempProfileData.state,
      })
      .eq('id', user.id);
    setSaveLoading(false);
    if (error) {
      setSaveError('Failed to save. Please try again.');
      return;
    }
    setProfileData(tempProfileData);
    setIsEditingProfile(false);
  };

  const handleCancelEdit = () => {
    setTempProfileData(profileData);
    setSaveError('');
    setIsEditingProfile(false);
  };

  const handleResetPassword = async () => {
    setPasswordError('');
    setPasswordSuccess(false);
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
    if (error) {
      setPasswordError(error.message);
      return;
    }
    setPasswordSuccess(true);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Header */}
      <Header onNavigate={onNavigate} />

      {/* Spacer for fixed header */}
      <div style={{ height: '64px' }}></div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className={brandStyles.text}>Account Settings</h2>
          <p className="text-gray-600 mt-1">Manage your profile information and security settings</p>
        </div>

        <div className="space-y-6">
          {/* Profile Information Section */}
          <Card className="border-2 border-gray-100">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription className="mt-1">Update your personal details and contact information</CardDescription>
                </div>
                {!isEditingProfile ? (
                  <Button
                    variant="outline"
                    className="rounded-lg"
                    onClick={() => setIsEditingProfile(true)}
                    disabled={profileLoading}
                  >
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="rounded-lg"
                      onClick={handleCancelEdit}
                      disabled={saveLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      className={brandStyles.primary}
                      onClick={handleSaveProfile}
                      disabled={saveLoading}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {saveLoading ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {profileLoading ? (
                <div className="text-sm text-gray-400 py-4">Loading…</div>
              ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={tempProfileData.firstName}
                      onChange={(e) => setTempProfileData({ ...tempProfileData, firstName: e.target.value })}
                      disabled={!isEditingProfile}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={tempProfileData.lastName}
                      onChange={(e) => setTempProfileData({ ...tempProfileData, lastName: e.target.value })}
                      disabled={!isEditingProfile}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email || ''}
                    disabled
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={tempProfileData.phone}
                    onChange={(e) => setTempProfileData({ ...tempProfileData, phone: e.target.value })}
                    disabled={!isEditingProfile}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      type="text"
                      value={tempProfileData.city}
                      onChange={(e) => setTempProfileData({ ...tempProfileData, city: e.target.value })}
                      disabled={!isEditingProfile}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      type="text"
                      value={tempProfileData.state}
                      onChange={(e) => setTempProfileData({ ...tempProfileData, state: e.target.value })}
                      disabled={!isEditingProfile}
                      className="mt-2"
                    />
                  </div>
                </div>

                {saveError && <p className="text-sm text-red-600">{saveError}</p>}
              </div>
              )}
            </CardContent>
          </Card>

          {/* Password Reset Section */}
          <Card className="border-2 border-gray-100">
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
              <CardDescription className="mt-1">Update your password to keep your account secure</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Separator />

                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="pr-10"
                      placeholder="Minimum 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-700 mb-1">Password Requirements</p>
                    <ul className="text-amber-600 space-y-1">
                      <li>• At least 8 characters long</li>
                      <li>• Use a mix of letters, numbers, and symbols</li>
                      <li>• Avoid common words or patterns</li>
                    </ul>
                  </div>
                </div>

                {passwordError && <p className="text-sm text-red-600">{passwordError}</p>}
                {passwordSuccess && <p className="text-sm text-green-700">Password updated successfully.</p>}

                <Button
                  className={brandStyles.primary}
                  onClick={handleResetPassword}
                  disabled={!passwordData.newPassword || !passwordData.confirmPassword}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Update Password
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account Preferences */}
          <Card className="border-2 border-gray-100">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Preferences</CardTitle>
                  <CardDescription className="mt-1">Customize your <span className="font-bold text-[#2B8B3B]">discgo</span> experience</CardDescription>
                </div>
                {prefSaved && (
                  <span className="flex items-center gap-1 text-xs text-[#2B8B3B] font-medium">
                    <Check className="w-3.5 h-3.5" />
                    Saved
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {profileLoading ? (
                <div className="text-sm text-gray-400 py-4">Loading…</div>
              ) : (
              <div className="space-y-4">
                {/* Email Notifications */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div>
                    <p className="font-medium text-sm">Email Notifications</p>
                    <p className="text-xs text-gray-600">Receive booking confirmations, cancellations, and refund updates</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.emailNotifications}
                    onChange={(e) => handlePrefChange('emailNotifications', e.target.checked)}
                    className="w-5 h-5 text-[#2B8B3B] rounded cursor-pointer"
                  />
                </div>

                {/* SMS Reminders — coming soon */}
                <div className="flex items-center justify-between py-3 border-b border-gray-100 opacity-50 select-none">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm text-gray-500">SMS Reminders</p>
                      <p className="text-xs text-gray-400">Get text reminders before your tee times — coming soon</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    disabled
                    className="w-5 h-5 rounded cursor-not-allowed"
                  />
                </div>

                {/* Marketing Communications */}
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-sm">Marketing Communications</p>
                    <p className="text-xs text-gray-600">Receive news, offers, and course recommendations</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketingCommunications}
                    onChange={(e) => handlePrefChange('marketingCommunications', e.target.checked)}
                    className="w-5 h-5 text-[#2B8B3B] rounded cursor-pointer"
                  />
                </div>
              </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
